import { z } from 'zod';

import { TranslatedTextSchema } from 'src/types/common';

export const TodoInfoSchema = z.object({
  todoId: z.number(),
  todoName: z.string(),
  startDate: z.string().nullable(),
  deadline: z.string().nullable(),
  effectiveStartDate: z.string().nullable(),
  effectiveDeadline: z.string().nullable(),
});

export const ExaminationStatisticsAnswerOptionSchema = z.object({
  answerId: z.number(),
  answerText: TranslatedTextSchema,
  isCorrect: z.boolean(),
  timesSelected: z.number(),
});

export const ExaminationStatisticsQuestionSchema = z.object({
  questionId: z.number(),
  questionText: TranslatedTextSchema,
  totalAnswers: z.number(),
  correctAnswers: z.number(),
  correctPercentage: z.number(),
  uniqueUsersAnswered: z.number(),
  answerOptions: z.array(ExaminationStatisticsAnswerOptionSchema),
});

export const ExaminationStatisticsSchema = z.object({
  examinationId: z.number(),
  examinationName: TranslatedTextSchema,
  todoInfo: TodoInfoSchema,
  usersInTodo: z.number(),
  questions: z.array(ExaminationStatisticsQuestionSchema),
  isSmedexLesson: z.boolean().default(true),
});

export type TodoInfo = z.infer<typeof TodoInfoSchema>;
export type ExaminationStatisticsAnswerOption = z.infer<
  typeof ExaminationStatisticsAnswerOptionSchema
>;
export type ExaminationStatisticsQuestion = z.infer<typeof ExaminationStatisticsQuestionSchema>;
export type ExaminationStatistics = z.infer<typeof ExaminationStatisticsSchema>;

export const AnswerOptionSchema = z.object({
  answerId: z.number(),
  text: TranslatedTextSchema,
  isCorrect: z.boolean(),
  isSelected: z.boolean(),
});

export const QuestionAttemptSchema = z.object({
  questionAttemptId: z.number(),
  questionId: z.number(),
  questionType: z.string(),
  questionText: TranslatedTextSchema,
  isCorrect: z.boolean(),
  timeSpentSeconds: z.number().nullable(),
  availableAnswers: z.array(AnswerOptionSchema),
});

export const AttemptDetailsSchema = z.object({
  attemptId: z.number(),
  userId: z.number(),
  userName: z.string(),
  examinationId: z.number(),
  passed: z.boolean(),
  score: z.number(),
  startedDate: z.string(),
  completedDate: z.string(),
  durationSeconds: z.number(),
  questions: z.array(QuestionAttemptSchema),
});

export const UserStatisticSchema = z.object({
  userId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  totalAttempts: z.number(),
  lastAttemptDate: z.string().nullable(),
  lastAttemptId: z.number().nullable(),
  lastAttemptDurationSeconds: z.number().nullable(),
  lastAttemptScore: z.number().nullable(),
  hasPassed: z.boolean(),
});

export const UserExaminationStatisticsSchema = z.object({
  examinationId: z.number(),
  examinationName: TranslatedTextSchema,
  passPercentage: z.number(),
  totalQuestions: z.number(),
  todoInfo: TodoInfoSchema,
  userStatistics: z.array(UserStatisticSchema),
});

export type AnswerOption = z.infer<typeof AnswerOptionSchema>;
export type QuestionAttempt = z.infer<typeof QuestionAttemptSchema>;
export type AttemptDetails = z.infer<typeof AttemptDetailsSchema>;
export type UserStatistic = z.infer<typeof UserStatisticSchema>;
export type UserExaminationStatistics = z.infer<typeof UserExaminationStatisticsSchema>;

// Mandatory Unit User Scores
export const MandatoryUnitUserSchema = z.object({
  userId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  groupIds: z.array(z.number()),
  completionPercentage: z.number(),
  hasEmail: z.boolean(),
});

export const LessonUserStatusSchema = z.object({
  userId: z.number(),
  completedDate: z.string().nullable(),
});

export const LessonUserCertificateStatusSchema = z.object({
  lessonId: z.number(),
  examinationId: z.number(),
  examinationName: TranslatedTextSchema,
  completionPercentage: z.number(),
  userStatuses: z.array(LessonUserStatusSchema),
});

export const MandatoryUnitUserCertificateStatusSchema = z.object({
  mandatoryUnitId: z.number(),
  users: z.array(MandatoryUnitUserSchema),
  lessons: z.array(LessonUserCertificateStatusSchema),
});

export type MandatoryUnitUser = z.infer<typeof MandatoryUnitUserSchema>;
export type LessonUserStatus = z.infer<typeof LessonUserStatusSchema>;
export type LessonUserCertificateStatus = z.infer<typeof LessonUserCertificateStatusSchema>;
export type MandatoryUnitUserCertificateStatus = z.infer<
  typeof MandatoryUnitUserCertificateStatusSchema
>;
